"use client";

import { useState } from "react";
import Link from "next/link";
import { track } from "@vercel/analytics";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader } from "lucide-react";

type PraxisContactModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PraxisContactModal({
  open,
  onOpenChange,
}: PraxisContactModalProps) {
  const [name, setName] = useState("");
  const [praxis, setPraxis] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/contact/praxis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          praxis: praxis.trim(),
          email: email.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Ein Fehler ist aufgetreten.");
        return;
      }

      track("praxis_contact_submit");
      setSuccess(true);
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName("");
      setPraxis("");
      setEmail("");
      setError(null);
      setSuccess(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-sky-50 rounded-3xl border-none max-w-md p-6">
        {success ? (
          <div className="flex flex-col gap-2 items-center text-center py-4">
            <DialogTitle className="sr-only">Anfrage gesendet</DialogTitle>
            <h2 className="text-headline-md text-black">
              Vielen Dank!
            </h2>
            <p className="text-copy text-black">
              Wir haben Deine Anfrage erhalten und melden uns in Kürze bei Dir.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 items-center text-center">
            <DialogTitle className="sr-only">Partner-Praxis werden</DialogTitle>
            <div className="flex flex-col gap-1">
              <p className="text-subline text-black">Partner-Praxis</p>
              <h2 className="text-headline-md text-black">
                Jetzt Kontakt aufnehmen
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dein Name"
                className="w-full !h-[46px] bg-transparent rounded-full px-5 border border-white !text-lg text-black placeholder:!text-lg placeholder:text-black"
                required
              />
              <Input
                value={praxis}
                onChange={(e) => setPraxis(e.target.value)}
                placeholder="Name der Praxis"
                className="w-full !h-[46px] bg-transparent rounded-full px-5 border border-white !text-lg text-black placeholder:!text-lg placeholder:text-black"
                required
              />
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder="E-Mail-Adresse"
                className="w-full !h-[46px] bg-transparent rounded-full px-5 border border-white !text-lg text-black placeholder:!text-lg placeholder:text-black"
                required
              />
              {error && (
                <p className="text-copy-sm text-red-500">{error}</p>
              )}
              <Button
                type="submit"
                variant="sun"
                size="sm"
                className="w-full h-[46px]"
                disabled={submitting}
              >
                {submitting ? (
                  <>Wird gesendet... <Loader className="h-4 w-4 animate-spin" /></>
                ) : (
                  "Anfrage senden"
                )}
              </Button>

              <p className="text-copy-sm text-center mt-1 mx-auto max-w-[80%]">
                Mit Absenden des Formulars stimme ich den{" "}
                <Link href="/datenschutz" className="underline">
                  Datenschutzbestimmungen
                </Link>{" "}
                zu
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
